import type { TxSummary } from '@apm/contracts';
import { memo } from 'react';
import { TX_TYPE_TONE } from '../live/format';
import { routeHref } from '../router';
import { STATUS_LEVEL_TEXT, statusLevel, statusText } from '../status';
import { TIP, TX_TYPE_LABEL } from '../terms';
import { formatCount, formatDuration, formatFull, formatStamp } from '../time';
import { Button, Cell, Chip, type Column, Lines, Row, Section, Table } from '../ui';
import { downloadCsv } from './csv';
import type { RectSelection } from './scale';

/**
 * X-View에서 사각형을 끌어 고른 요청 목록 (6회차 보완 결정 3).
 *
 * 행을 누르면 `#/tx/{요청 번호}` 상세로 간다 (7회차 결정 3).
 * 상한을 넘으면 자르고 몇 건이 더 있는지만 알린다. 수만 행을 화면에 올리지 않는다.
 *
 * 19회차에 CSV 내보내기가 붙었다(후보 #7). 받는 것은 지금 표에 보이는 것과 같은 범위다.
 */
const COLUMNS: readonly Column[] = [
  { label: '종료 시각', width: 'w-40' },
  { label: '유형', width: 'w-28' },
  { label: '요청 경로', title: TIP.entry },
  { label: '응답시간', width: 'w-28' },
  { label: '상태', width: 'w-32' },
  { label: 'DB 오류', width: 'w-32' },
  { label: '회사(테넌트)', width: 'w-28', title: TIP.tenant },
];

/**
 * 행이 최대 2,000개(`SELECTION_LIMIT`)라 `memo`로 둔다 (D-C3). 분포도 구획은 실시간 반영마다 건수를
 * 다시 세며 그려지는데, 고른 범위가 그대로면 표는 따라 그리지 않는다
 */
export const SelectionTable = memo(function SelectionTable({
  selection,
  back,
  onPick,
  onClear,
}: {
  selection: RectSelection;
  /** 상세 링크에 실을 복귀 조건(지금 주소의 조건). 바뀌면 링크를 다시 만든다 (R62-2) */
  back?: string;
  onPick: (row: TxSummary) => void;
  onClear: () => void;
}) {
  const hidden = selection.total - selection.rows.length;

  return (
    <Section
      boxed
      title={`끌어 고른 요청 ${formatCount(selection.total)}건`}
      note={
        <span className="flex flex-wrap gap-x-3 gap-y-1">
          {selection.byType.map((item) => (
            <span key={item.type}>
              {TX_TYPE_LABEL[item.type]} {formatCount(item.count)}건
            </span>
          ))}
        </span>
      }
      actions={
        <>
          <Button size="sm" onClick={() => downloadCsv(selection.rows)}>
            CSV로 받기
          </Button>
          <Button size="sm" onClick={onClear}>
            선택 해제 (Esc)
          </Button>
        </>
      }
    >
      <Table
        columns={COLUMNS}
        caption="끌어 고른 요청"
        minWidth="min-w-[880px]"
        maxHeight="max-h-[420px]"
      >
        {selection.rows.map((row) => {
          const level = statusLevel(row);
          return (
            // 첫 칸의 링크가 키보드 경로다. 행 전체의 누르기는 마우스 편의로 남긴다 (13회차)
            <Row key={row.txId} onClick={() => onPick(row)}>
              <Cell nowrap tone="muted" className="tabular">
                <a
                  href={routeHref({
                    name: 'detail',
                    traceId: row.traceId,
                    from: 'requests',
                    service: row.service,
                    back,
                  })}
                  title={formatFull(row.startedAt + row.durationMs)}
                  className="-mx-1 block rounded px-1 text-inherit no-underline"
                >
                  {formatStamp(row.startedAt + row.durationMs)}
                </a>
              </Cell>
              <Cell>
                <Chip tone={TX_TYPE_TONE[row.txType]}>{TX_TYPE_LABEL[row.txType]}</Chip>
              </Cell>
              <Cell mono title={row.entry} className="max-w-[320px] truncate">
                {row.entry}
              </Cell>
              <Cell mono nowrap className="tabular">
                {formatDuration(row.durationMs)}
              </Cell>
              <Cell nowrap className={STATUS_LEVEL_TEXT[level]}>
                {statusText(row)}
              </Cell>
              <Cell nowrap className="tabular">
                {row.sqlErrorCount > 0 ? (
                  <span className="text-error">
                    {row.sqlErrorCount}건{row.sqlErrorCode ? ` (${row.sqlErrorCode})` : ''}
                  </span>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </Cell>
              <Cell tone="muted" className="tabular">
                {row.tenantId ?? '-'}
              </Cell>
            </Row>
          );
        })}
      </Table>

      {hidden > 0 ? (
        <p className="tabular mt-3 text-xs text-muted">
          <Lines
            lines={[
              `${formatCount(hidden)}건 더 있다. 영역을 좁혀서 다시 선택할 것.`,
              'CSV도 표에 보이는 것까지만 담는다',
            ]}
          />
        </p>
      ) : null}
    </Section>
  );
});
